import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import instance from '../api'
import { useAuthContext } from '../../context/auth/auth'

/**
 * Super admin confirms a bank transfer arrived. The backend starts (or
 * extends) the subscription period, writes a Payment row, generates the
 * invoice PDF and emails the customer - so this is the only correct way to
 * activate. Flipping `organization.is_active` would skip all of it.
 *
 * `amount` overrides the plan price for a negotiated figure; omitted here, so
 * the backend falls back to the plan price.
 */
const activateSubscription = async (subscriptionId: number, token: string) => {
  return instance.post(
    `/payment/subscriptions/${subscriptionId}/activate/`,
    {},
    { headers: { Authorization: `Bearer ${token}` } },
  )
}

const useActivateSubscription = (organizationId: string) => {
  const { token } = useAuthContext()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: (subscriptionId: number) =>
      activateSubscription(subscriptionId, token!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['organization', organizationId],
      })
      await queryClient.invalidateQueries({ queryKey: ['organizations'] })
      toast.success(t('accountActivatedSuccessfully'))
    },
    onError: (error) => {
      // The backend explains refusals (wrong provider, non-activatable
      // status, not a super admin); surface that rather than a generic
      // failure the operator cannot act on.
      const message = axios.isAxiosError(error)
        ? error.response?.data?.data?.message
        : undefined
      toast.error(message || t('failedToActivateAccount'))
    },
  })
}

export default useActivateSubscription
